import { profileRepo, type ProfileRecord } from './profile.repo'
import { config } from '@/config/env'
import { getSdkOrAppToken } from '@/lib/sdk-session-token'

const API = config.apiUrl
const SDK = import.meta.env.VITE_SDK_API_URL || API

/**
 * Mirror a saved name onto the chat contact, so other people see it in Presence.
 *
 * Chat shows the name kept on the SDK contact, not this one. Best effort: the
 * inMeet save has already succeeded, and a user who has not accepted the chat
 * terms (403) has no contact yet; registration picks the name up then.
 */
async function syncNameToSdk(name: string): Promise<void> {
  try {
    const token = await getSdkOrAppToken()
    if (!token) return
    await fetch(`${SDK}/api/v1/contacts/self/profile`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName: name }),
    })
  } catch {
    // Best effort, see above.
  }
}

function getToken(): string {
  try {
    const raw = localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (raw) {
      const parsed = JSON.parse(raw) as any
      const session = parsed?.session
      if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
      return session?.accessToken || ''
    }
  } catch {}
  return ''
}

interface ApiUser {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  /** The permission level (`org_admin` | `member`), NOT the profile's Role. */
  role: string
  /** The profile's free-text Role ("Product Designer"); null when none is set. */
  jobTitle?: string | null
  phone?: string | null
  location?: string | null
  orgId: string | null
}

/** A field the server may not have yet: its value when it sends one (null
 *  meaning "not added"), the local copy only when the server predates it. */
const fromServer = (value: string | null | undefined, local: string): string =>
  value === undefined ? local : (value ?? '')

/** Merge the DB-backed user into the local profile shape. */
function mergeApiUser(api: ApiUser, local: ProfileRecord): ProfileRecord {
  return {
    name:      api.name      ?? local.name,
    email:     api.email     ?? local.email,
    // The server is AUTHORITATIVE for the picture, including when it says
    // null. Falling back to the local copy would resurrect two dead things:
    // the seeded stock photo, and the data-URL "previews" the old camera
    // button wrote to localStorage — so removing your picture would appear
    // to fail, and a stale preview would outrank the real uploaded image.
    avatarUrl: api.avatarUrl ?? '',
    // From `jobTitle`, never `role`. This used to read `api.role`, the
    // permission level, which is always set, so every typed Role was replaced
    // by "member" the moment it was saved and again on every load.
    role:      fromServer(api.jobTitle, local.role),
    // Server-held since phone and location moved off localStorage, which also
    // stops the old seeded phone number (one employee's) showing for anyone.
    phone:     fromServer(api.phone, local.phone),
    location:  fromServer(api.location, local.location),
  }
}

/** The fields the server keeps besides the name, by their profile names. */
const SERVER_FIELDS = ['role', 'phone', 'location'] as const

/**
 * What a profile save sends: the name, plus each server-held field the user
 * actually changed. Unchanged ones stay out, so a save still works on a
 * database that has not had the migrations adding their columns yet.
 */
export function profilePatch(before: ProfileRecord, after: ProfileRecord): Partial<ProfileRecord> {
  const patch: Partial<ProfileRecord> = { name: after.name }
  for (const k of SERVER_FIELDS) {
    if (after[k] !== before[k]) patch[k] = after[k]
  }
  return patch
}

export const profileService = {
  async get(): Promise<ProfileRecord> {
    const local = await profileRepo.get()
    const token = getToken()
    if (!token) return local

    try {
      const res = await fetch(`${API}/api/v1/users/me`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return local
      const body = await res.json()
      return mergeApiUser(body.user as ApiUser, local)
    } catch {
      return local
    }
  },
  /**
   * Save the profile.
   *
   * `name`, `role` (as `jobTitle`), `phone` and `location` go to the server.
   * This used to write only to localStorage, so an edited name survived until
   * the next `get()` — which merges the API's value over the local one — and
   * then reverted. It looked saved and was not.
   *
   * The local copy is written either way, so the screen is right immediately
   * and a failed request still leaves what was typed rather than reverting it
   * under the user.
   */
  async update(patch: Partial<ProfileRecord>): Promise<ProfileRecord> {
    const local = await profileRepo.update(patch)

    const token = getToken()
    if (!token || (patch.name === undefined && SERVER_FIELDS.every((k) => patch[k] === undefined))) return local

    try {
      const res = await fetch(`${API}/api/v1/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: patch.name, jobTitle: patch.role, phone: patch.phone, location: patch.location }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message || `Could not save your profile (${res.status})`)
      }
      const body = await res.json()
      // Return the server's record, so the caller shows what was actually
      // stored rather than what it hoped would be.
      const saved = mergeApiUser(body.user as ApiUser, local)
      if (saved.name) void syncNameToSdk(saved.name)
      return saved
    } catch (err) {
      // Surfaced rather than swallowed: a save that silently did not persist is
      // the bug this replaces.
      throw err instanceof Error ? err : new Error('Could not save your profile')
    }
  },
  async resetAvatar(): Promise<ProfileRecord> {
    return profileRepo.update({ avatarUrl: '' })
  },
}

export type { ProfileRecord } from './profile.repo'
